import { FilesetResolver, LlmInference } from "@mediapipe/tasks-genai";
import { appConfig } from "./config.js";
import {
  LLMWrapper,
  ModelFamily,
  MediaPipeRuntime,
} from "./llm-wrapper.js";
import { TransformersRuntime } from "./transformers-runtime.js";

function detectFamilyFromUrl(url: string): ModelFamily {
  if (/qwen/i.test(url)) return "qwen";
  if (/gemma-?4/i.test(url)) return "gemma4";
  return "gemma3";
}

export class ModelLoader {
  private modelCached: boolean = false;

  async fetchModelWithProgress(
    url: string,
    onProgress: (status: string, progress?: number) => void,
  ): Promise<string> {
    if (this.modelCached) {
      onProgress("Loading AI from cache...", 90);
    }

    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to fetch model: ${response.statusText}`);
    }

    const contentLength = response.headers.get("content-length");
    const total = parseInt(contentLength || "0", 10);
    if (!response.body) {
      throw new Error("No response body");
    }

    const reader = response.body.getReader();
    const chunks: Uint8Array[] = [];
    let receivedLength = 0;

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      chunks.push(value);
      receivedLength += value.length;
      if (total > 0 && !this.modelCached) {
        const progress = Math.round((receivedLength / total) * 100);
        onProgress(`Downloading AI... ${progress}%`, progress);
      }
    }

    const chunksAll = new Uint8Array(receivedLength);
    let position = 0;
    for (const chunk of chunks) {
      chunksAll.set(chunk, position);
      position += chunk.length;
    }

    const blob = new Blob([chunksAll]);
    return URL.createObjectURL(blob);
  }

  async initializeModel(
    onStatus: (status: "loading" | "ready" | "error", text: string) => void,
    onProgress: (text: string, progress?: number) => void,
  ): Promise<LLMWrapper> {
    // Mobile classification: any narrow viewport gets the smaller Qwen.
    // Catches phones (iOS + Android) and tablets in portrait.
    const MOBILE_BREAKPOINT_PX = 768;
    const isMobile =
      window.innerWidth < MOBILE_BREAKPOINT_PX ||
      (/iPad|iPhone|iPod|Android/.test(navigator.userAgent) &&
        !(window as any).MSStream);

    if (isMobile) {
      const tagline = document.getElementById("tagline");
      if (tagline) {
        tagline.innerHTML =
          'All the Trust and Safety <s style="opacity: 0.5">Gemma 4</s> Gemma 3 270M Can Deliver';
      }
    }

    onStatus("loading", "Loading AI Model...");
    onProgress("Restoring intellect...", 0);

    console.log(
      `[model-loader] isMobile=${isMobile} (innerWidth=${window.innerWidth}, UA mobile=${/iPad|iPhone|iPod|Android/.test(navigator.userAgent)})`,
    );

    if (isMobile) {
      // Mobile path: MediaPipe + Qwen .task file from MODEL_BASE_URL.
      console.log("[model-loader] taking mobile (MediaPipe + Qwen) path");
      return this.loadMediaPipe(onStatus, onProgress, /*forceMobile*/ true);
    }
    console.log("[model-loader] taking desktop (transformers.js + Gemma 4) path");
    // Desktop path: transformers.js + Gemma 4 ONNX, fetched directly from HF Hub.
    // No MODEL_BASE_URL needed for desktop anymore.
    try {
      return await this.loadTransformers(onStatus, onProgress);
    } catch (err) {
      console.warn(
        "Transformers.js path failed; falling back to MediaPipe:",
        err,
      );
      return this.loadMediaPipe(onStatus, onProgress, /*forceMobile*/ false);
    }
  }

  private async loadTransformers(
    onStatus: (status: "loading" | "ready" | "error", text: string) => void,
    onProgress: (text: string, progress?: number) => void,
  ): Promise<LLMWrapper> {
    const modelId = appConfig.transformersModelId;
    onStatus("loading", "Preparing MURD.AI...");
    onProgress("Preparing MURD.AI...", 5);

    const runtime = await TransformersRuntime.load(
      modelId,
      "gemma4",
      ({ pct, text }) => onProgress(text, pct),
    );

    onStatus("ready", "MURD.AI Ready!");
    console.log("🤖 Runtime: transformers.js, family: gemma4");
    return new LLMWrapper(runtime);
  }

  private async loadMediaPipe(
    onStatus: (status: "loading" | "ready" | "error", text: string) => void,
    onProgress: (text: string, progress?: number) => void,
    forceMobile: boolean,
  ): Promise<LLMWrapper> {
    if (!(window as any).MODEL_BASE_URL) {
      onStatus("error", "Misconfigured - No .env file");
      throw new Error(
        "MODEL_BASE_URL not configured. Please create a .env file.",
      );
    }

    const genai = await FilesetResolver.forGenAiTasks(
      "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-genai@0.10.27/wasm",
    );

    onStatus(
      "loading",
      this.modelCached ? "Loading cached model..." : "Preparing MURD.AI...",
    );
    onProgress(
      this.modelCached ? "Loading cached model..." : "Preparing MURD.AI...",
      0,
    );

    const url = forceMobile
      ? appConfig.mobileModelUrl
      : appConfig.modelUrl;
    const family = detectFamilyFromUrl(url);
    const visionEnabled = family === "gemma4";

    onStatus(
      "loading",
      this.modelCached ? "Loading cached model..." : "Downloading model...",
    );
    const modelPath = await this.fetchModelWithProgress(url, onProgress);

    onStatus("loading", "Initializing AI...");
    onProgress("Initializing AI...", 100);

    // KV cache size is baked into the .task file. Gemma 3 270M handles
    // 2048 fine; Gemma 4 E2B can handle 8192.
    const maxTokens = family === "gemma3" ? 2048 : 8192;
    console.log(
      `[model-loader] LlmInference.createFromOptions: family=${family} maxTokens=${maxTokens} vision=${visionEnabled}`,
    );
    const llm = await LlmInference.createFromOptions(genai, {
      baseOptions: {
        modelAssetPath: modelPath,
        delegate: "GPU",
      },
      maxTokens,
      temperature: 1.0,
      topK: 64,
      topP: 0.95,
      randomSeed: Math.floor(Math.random() * 10000),
      ...(visionEnabled ? { maxNumImages: 1 } : {}),
    });

    URL.revokeObjectURL(modelPath);

    onStatus(
      "ready",
      this.modelCached ? "MURD.AI Ready! (Cached)" : "MURD.AI Ready!",
    );
    console.log(`🤖 Runtime: mediapipe, family: ${family}`);
    return new LLMWrapper(new MediaPipeRuntime(llm, family, visionEnabled));
  }

  setModelCached(cached: boolean): void {
    this.modelCached = cached;
  }

  isModelCached(): boolean {
    return this.modelCached;
  }
}
